import React, { useState } from 'react'
import { Modal } from '../components/common/Modal'

interface CookiesModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CookiesModal({ isOpen, onClose }: CookiesModalProps): React.ReactElement {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: true,
    marketing: false,
  })

  const handleChange = (key: keyof typeof preferences) => {
    if (key !== 'essential') {
      setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
    }
  }

  const handleSave = () => {
    // Save preferences to localStorage or send to backend
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences))
    onClose()
  }

  const cookieTypes = [
    {
      name: 'Essential',
      description: 'Required for basic functionality like login, security, and account management.',
      required: true,
    },
    {
      name: 'Analytics',
      description: 'Help us understand how you use BabyKingdom to improve the experience. No personal data is shared.',
      required: false,
    },
    {
      name: 'Marketing',
      description: 'Used to show relevant content and offers. We never track children with marketing cookies.',
      required: false,
    },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cookie Preferences" size="md">
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-3">🍪</div>
          <h3 className="text-2xl font-black text-purple-700 mb-2">Manage Your Cookie Preferences</h3>
          <p className="text-gray-700 font-bold">
            Control how we use cookies to enhance your experience
          </p>
        </div>

        {/* Cookie Types */}
        <div className="space-y-4">
          {cookieTypes.map((cookie, idx) => (
            <div key={idx} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-4 border-purple-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-black text-purple-700 mb-1">{cookie.name} Cookies {cookie.required ? '(Required)' : ''}</h4>
                  <p className="text-gray-700 font-semibold text-sm">{cookie.description}</p>
                </div>
                <div className="flex-shrink-0">
                  {cookie.required ? (
                    <span className="px-4 py-2 bg-purple-400 text-white font-black rounded-full text-sm">
                      ✓
                    </span>
                  ) : (
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences[cookie.name.toLowerCase() as keyof typeof preferences] || false}
                        onChange={() => handleChange(cookie.name.toLowerCase() as keyof typeof preferences)}
                        className="w-5 h-5 accent-purple-500 cursor-pointer"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Information */}
        <div className="p-4 bg-blue-50 rounded-2xl border-4 border-blue-200">
          <h4 className="font-black text-blue-700 mb-3">ℹ️ What are Cookies?</h4>
          <p className="text-gray-700 font-semibold text-sm mb-3">
            Cookies are small text files stored on your device that help us provide a better experience. We use them to:
          </p>
          <ul className="space-y-2">
            <li className="text-gray-700 font-bold text-sm flex gap-2">
              <span>✓</span>
              <span>Remember your preferences</span>
            </li>
            <li className="text-gray-700 font-bold text-sm flex gap-2">
              <span>✓</span>
              <span>Keep you logged in</span>
            </li>
            <li className="text-gray-700 font-bold text-sm flex gap-2">
              <span>✓</span>
              <span>Understand usage patterns</span>
            </li>
            <li className="text-gray-700 font-bold text-sm flex gap-2">
              <span>✓</span>
              <span>Prevent fraud</span>
            </li>
          </ul>
        </div>

        {/* Privacy Note */}
        <div className="p-4 bg-green-50 rounded-2xl border-4 border-green-200">
          <h4 className="font-black text-green-700 mb-2">🛡️ Child Privacy</h4>
          <p className="text-gray-700 font-bold text-sm">
            We NEVER use cookies to track children for marketing purposes. All cookies respect COPPA compliance and children&apos;s privacy rights.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 pt-4 border-t-4 border-purple-200">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-gray-300 text-gray-800 font-black rounded-full hover:bg-gray-400 transition"
          >
            Reject All
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2 bg-gradient-to-r from-purple-400 to-pink-400 text-white font-black rounded-full hover:from-purple-500 hover:to-pink-500 transition"
          >
            Accept & Save
          </button>
        </div>

        <div className="text-center">
          <a href="/privacy" className="text-purple-500 font-black hover:text-purple-700 underline text-sm">
            Read our full Privacy Policy
          </a>
        </div>
      </div>
    </Modal>
  )
}
